import type { DragEventHandler, ChangeEventHandler, RefObject } from 'react';
import { Upload } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';

interface DropZoneCardProps {
  previewUrl: string | null;
  isDragOver: boolean;
  onDragOver: DragEventHandler<HTMLLabelElement>;
  onDragLeave: DragEventHandler<HTMLLabelElement>;
  onDrop: DragEventHandler<HTMLLabelElement>;
  inputRef: RefObject<HTMLInputElement | null>;
  onFileChange: ChangeEventHandler<HTMLInputElement>;
}

export function DropZoneCard({
  previewUrl,
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  inputRef,
  onFileChange,
}: DropZoneCardProps) {
  const { t } = useTranslation();

  return (
    <label
      className={cn(
        'flex min-h-64 w-full cursor-pointer flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed p-8 transition-colors has-[input:focus-visible]:ring-[3px] has-[input:focus-visible]:ring-ring/50',
        isDragOver ? 'border-primary bg-primary/5' : 'border-border bg-muted/30'
      )}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onFileChange}
        className="sr-only"
      />

      {previewUrl ? (
        <img
          src={previewUrl}
          alt={t('traceability.uploadImage.imageAlt')}
          className="max-h-80 w-full rounded-xl object-contain"
        />
      ) : (
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Upload className="h-7 w-7" />
          </span>
          <div className="space-y-1">
            <p className="text-base font-medium text-foreground">
              {t('traceability.uploadImage.dropZone.prompt')}
            </p>
            <p className="text-sm text-primary underline underline-offset-2">
              {t('traceability.uploadImage.dropZone.browse')}
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('traceability.uploadImage.dropZone.hint')}
          </p>
        </div>
      )}
    </label>
  );
}
